import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfileFlags } from '../api/onboarding';
import { useOnboarding } from './OnboardingContext';

export function FitnessTestStep({ stepId }) {
  const { authUser, nextStep, markStepComplete, setFlags, setLoading, loading } = useOnboarding();
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const persistChoice = async (pending, callback) => {
    if (!authUser) {
      callback();
      return;
    }

    setError('');
    setLoading(true);
    try {
      const { error: flagError } = await updateProfileFlags(authUser.id, {
        fitness_test_pending: pending,
      });

      if (flagError) {
        setError(flagError.message || 'Failed to save preference.');
        return;
      }

      setFlags({ fitness_test_pending: pending });
      markStepComplete(stepId);
      callback();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartNow = () => {
    persistChoice(false, () => navigate('/fitness-test'));
  };

  const handleDoLater = () => {
    persistChoice(true, () => nextStep());
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-md p-10 border border-gray-100 space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Baseline Fitness Test</h2>
          <p className="text-sm text-gray-600 mt-2">
            Take a quick assessment so we can tailor intensity, volume, and recovery guidance from day one.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⏱</span>
            <div>
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                What to expect
              </h3>
              <ul className="text-sm text-gray-600 list-disc list-inside mt-2 space-y-1">
                <li>5-minute warm-up and readiness check.</li>
                <li>3 strength markers (push, pull, legs).</li>
                <li>Short cardio interval to gauge endurance.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            data-testid="onboarding-start-fitness-test"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            onClick={handleStartNow}
            disabled={loading}
          >
            {loading ? 'Preparing…' : 'Start Fitness Test Now'}
          </button>
          <button
            type="button"
            data-testid="onboarding-skip-fitness-test"
            className="w-full bg-white border border-gray-300 hover:border-gray-400 text-gray-800 font-semibold px-6 py-3 rounded-xl transition-colors"
            onClick={handleDoLater}
            disabled={loading}
          >
            Do This Later
          </button>
        </div>
      </div>
    </div>
  );
}
